import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Autocomplete, OutlinedInput } from "~/components/UI";
import { useField, FormikHandlers } from "formik";
import _ from "lodash";
import api from "~/api";

interface FromikSelectContractProps {
  name: string;
  defaultInputValue?: string;
  filterOptions?: (
    options: ContractsApi.ContractDto[],
    state: any
  ) => ContractsApi.ContractDto[];
}

interface SelectContractProps {
  inputValue?: string;
  name?: string;
  onChange?: (contract?: ContractsApi.ContractDto) => void;
  onChangeValue?: (value?: string) => void;
  onBlur?: FormikHandlers["handleBlur"];
  error?: string | undefined;
  touched?: boolean;
  autoClear?: boolean;
  filterOptions?: (
    options: ContractsApi.ContractDto[],
    state: any
  ) => ContractsApi.ContractDto[];
}

const requestSearchContract = (search: string) =>
  api.contracts.getAll(20, 1, { search });

export const SelectContract = React.memo(
  ({
    name = "",
    onChange,
    onChangeValue,
    onBlur = () => {},
    touched,
    error,
    filterOptions,
    inputValue,
  }: SelectContractProps) => {
    const [value, setValue] = useState<string | undefined>(inputValue || "");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<
      ContractsApi.ContractDto[]
    >([]);
    const [contract, setContract] =
      useState<Partial<ContractsApi.ContractDto> | null>(null);

    const searchContractes = useMemo(
      () =>
        _.debounce((contractId: string) => {
          setLoading(true);
          requestSearchContract(contractId)
            .then(({ data }) => {
              setSearchResults(data.data);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 500),
      []
    );

    const onChangeInputValue = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setValue(e.target.value);
      onChangeValue && onChangeValue(e.target.value);
      searchContractes(e.target.value || "");
    };

    const onChangeAutocomplete = useCallback(
      (_e: React.ChangeEvent<{}>, newValue: any) => {
        if (newValue) {
          setValue(newValue.name);
          setContract(newValue);
        } else {
          setValue("");
          setContract(null);
        }
        onChange && onChange(newValue);
      },
      [onChange]
    );

    const getOptionSelected = useCallback(
      (o: ContractsApi.ContractDto, value: ContractsApi.ContractDto) =>
        o.contractId === value.contractId,
      []
    );

    const getOptionLabel = useCallback(
      (o: ContractsApi.ContractDto) => o.contractName || "",
      []
    );

    useEffect(() => {
      if (name && !inputValue) {
        setValue("");
        setContract(null);
      }
    }, [name, inputValue]);

    useEffect(() => {
      searchContractes("");
    }, [searchContractes]);

    return (
      <Autocomplete
        value={contract}
        onChange={onChangeAutocomplete}
        getOptionSelected={getOptionSelected}
        onBlur={onBlur}
        noOptionsText="No Contracts"
        options={searchResults}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        filterSelectedOptions={true}
        disableClearable
        loading={loading}
        renderInput={(params: any) => (
          <OutlinedInput
            className={params.InputProps.className}
            endAdornment={params.InputProps.endAdornment}
            inputProps={_.omit(params.inputProps, "value")}
            value={params.inputProps.value || value}
            touched={touched}
            error={error}
            fullWidth
            onChange={onChangeInputValue}
          />
        )}
      />
    );
  }
);

const FromikContainer = ({
  name,
  defaultInputValue,
  ...other
}: FromikSelectContractProps & SelectContractProps) => {
  const [field, meta] = useField(name);
  const { error, touched } = meta;

  const onChange = useCallback(
    (contract?: ContractsApi.ContractDto) => {
      field.onChange({
        target: {
          name,
          value: contract ? contract.contractId : "",
        },
      });
    },
    [field, name]
  );

  return (
    <SelectContract
      {...field}
      {...other}
      onChange={onChange}
      touched={touched}
      error={error}
      inputValue={defaultInputValue || field.value}
    />
  );
};

export default React.memo(FromikContainer);
